import { useState, useCallback } from 'react'
import type { DataTableItem } from '../types'

type RowId = string | number

interface UseDataTableSelectionOptions<T extends DataTableItem> {
  data: T[]
  getRowId?: (row: T) => RowId
}

interface UseDataTableSelectionReturn {
  selectedIds: Set<RowId>
  toggle: (id: RowId) => void
  selectAll: () => void
  clearSelection: () => void
  isSelected: (id: RowId) => boolean
  selectedCount: number
}

export function useDataTableSelection<T extends DataTableItem>({
  data,
  getRowId = (row) => row.id
}: UseDataTableSelectionOptions<T>): UseDataTableSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<RowId>>(new Set())

  const toggle = useCallback((id: RowId) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(data.map(getRowId)))
  }, [data, getRowId])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
  }, [])

  const isSelected = useCallback((id: RowId) => {
    return selectedIds.has(id)
  }, [selectedIds])

  return {
    selectedIds,
    toggle,
    selectAll,
    clearSelection,
    isSelected,
    selectedCount: selectedIds.size
  }
}
